import {OcrRegion} from './types';

export const MIN_REGION_SIZE = 8;
export type ResizeHandle = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w';
export type SplitAxis = 'horizontal' | 'vertical';

function newRegionId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `00000000-0000-4000-8000-${Math.random().toString(16).slice(2).padEnd(12, '0').slice(0, 12)}`;
}

export function cloneRegions(regions: OcrRegion[]): OcrRegion[] {
  return regions.map(region => ({...region, bbox: [...region.bbox] as OcrRegion['bbox']}));
}

export function sortRegions(regions: OcrRegion[]): OcrRegion[] {
  const rows: {top: number; bottom: number; height: number; regions: OcrRegion[]}[] = [];
  [...regions]
    .sort((a, b) => a.bbox[1] - b.bbox[1] || a.bbox[0] - b.bbox[0] || a.bbox[3] - b.bbox[3])
    .forEach(region => {
      const [, y1, , y2] = region.bbox;
      const height = y2 - y1;
      let best: typeof rows[number] | undefined;
      let bestOverlap = 0;
      rows.forEach(row => {
        const overlap = Math.max(0, Math.min(y2, row.bottom) - Math.max(y1, row.top));
        const required = 0.5 * Math.min(height, row.height);
        if (overlap >= required && overlap > bestOverlap) {
          best = row;
          bestOverlap = overlap;
        }
      });
      if (!best) {
        rows.push({top: y1, bottom: y2, height, regions: [region]});
      } else {
        best.regions.push(region);
        best.top = Math.min(best.top, y1);
        best.bottom = Math.max(best.bottom, y2);
        best.height = Math.min(best.height, height);
      }
    });
  return rows
    .sort((a, b) => a.top - b.top)
    .flatMap(row => row.regions.sort((a, b) => a.bbox[0] - b.bbox[0] || a.bbox[1] - b.bbox[1]));
}

export function regionsEqual(left: OcrRegion[], right: OcrRegion[]): boolean {
  if (left.length !== right.length) return false;
  return left.every((region, index) => {
    const candidate = right[index];
    return candidate && region.id === candidate.id && region.type === candidate.type && region.text === candidate.text
      && region.bbox.every((value, coordinate) => value === candidate.bbox[coordinate]);
  });
}

export function normalizedPoint(clientX: number, clientY: number, rect: Pick<DOMRect, 'left' | 'top' | 'width' | 'height'>): [number, number] {
  const x = Math.round((clientX - rect.left) / rect.width * 1000);
  const y = Math.round((clientY - rect.top) / rect.height * 1000);
  return [Math.max(0, Math.min(1000, x)), Math.max(0, Math.min(1000, y))];
}

export function drawBox(start: [number, number], end: [number, number]): OcrRegion['bbox'] | null {
  const bbox: OcrRegion['bbox'] = [
    Math.min(start[0], end[0]), Math.min(start[1], end[1]),
    Math.max(start[0], end[0]), Math.max(start[1], end[1]),
  ];
  return bbox[2] - bbox[0] >= MIN_REGION_SIZE && bbox[3] - bbox[1] >= MIN_REGION_SIZE ? bbox : null;
}

export function createRegion(bbox: OcrRegion['bbox'], type: OcrRegion['type'] = 'handwritten'): OcrRegion {
  return {id: newRegionId(), bbox, type, text: ''};
}

export function moveRegion(region: OcrRegion, deltaX: number, deltaY: number): OcrRegion {
  const width = region.bbox[2] - region.bbox[0];
  const height = region.bbox[3] - region.bbox[1];
  const x1 = Math.max(0, Math.min(1000 - width, region.bbox[0] + deltaX));
  const y1 = Math.max(0, Math.min(1000 - height, region.bbox[1] + deltaY));
  return {...region, bbox: [x1, y1, x1 + width, y1 + height]};
}

export function resizeRegion(region: OcrRegion, handle: ResizeHandle, deltaX: number, deltaY: number): OcrRegion {
  let [x1, y1, x2, y2] = region.bbox;
  if (handle.includes('w')) x1 = Math.max(0, Math.min(x2 - MIN_REGION_SIZE, x1 + deltaX));
  if (handle.includes('e')) x2 = Math.min(1000, Math.max(x1 + MIN_REGION_SIZE, x2 + deltaX));
  if (handle.includes('n')) y1 = Math.max(0, Math.min(y2 - MIN_REGION_SIZE, y1 + deltaY));
  if (handle.includes('s')) y2 = Math.min(1000, Math.max(y1 + MIN_REGION_SIZE, y2 + deltaY));
  return {...region, bbox: [x1, y1, x2, y2]};
}

export function mergeRegions(regions: OcrRegion[], selectedIds: Set<string>): OcrRegion[] {
  const selected = sortRegions(regions.filter(region => selectedIds.has(region.id)));
  if (selected.length < 2) return regions;
  const merged: OcrRegion = {
    id: selected[0].id,
    bbox: [
      Math.min(...selected.map(region => region.bbox[0])),
      Math.min(...selected.map(region => region.bbox[1])),
      Math.max(...selected.map(region => region.bbox[2])),
      Math.max(...selected.map(region => region.bbox[3])),
    ],
    type: selected[0].type,
    text: selected.map(region => region.text).filter(Boolean).join('\n'),
  };
  return sortRegions([...regions.filter(region => !selectedIds.has(region.id)), merged]);
}

export function splitRegion(
  regions: OcrRegion[], regionId: string, axis: SplitAxis, position: number, textOffset: number,
): OcrRegion[] | null {
  const source = regions.find(region => region.id === regionId);
  if (!source) return null;
  const [x1, y1, x2, y2] = source.bbox;
  const boundedOffset = Math.max(0, Math.min(source.text.length, textOffset));
  let firstBox: OcrRegion['bbox'];
  let secondBox: OcrRegion['bbox'];
  if (axis === 'horizontal') {
    if (position - y1 < MIN_REGION_SIZE || y2 - position < MIN_REGION_SIZE) return null;
    firstBox = [x1, y1, x2, position];
    secondBox = [x1, position, x2, y2];
  } else {
    if (position - x1 < MIN_REGION_SIZE || x2 - position < MIN_REGION_SIZE) return null;
    firstBox = [x1, y1, position, y2];
    secondBox = [position, y1, x2, y2];
  }
  const first = {...source, bbox: firstBox, text: source.text.slice(0, boundedOffset)};
  const second = {...source, id: newRegionId(), bbox: secondBox, text: source.text.slice(boundedOffset)};
  return sortRegions(regions.flatMap(region => region.id === regionId ? [first, second] : [region]));
}
